/**
 * Training Session Detail Page.
 *
 * Shows training session metadata, canonical knowledge, ingestion batches,
 * public interview sessions, and transcript history.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  ExternalLink,
  GraduationCap,
  LayoutList,
  Loader2,
  MessageSquare,
  Plus,
  RotateCcw,
  Trash2,
  Upload,
  User,
  Users,
} from 'lucide-react';

import { PageHeader } from '@/components/layout/PageHeader';
import { Markdown } from '@/components/common/Markdown';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import {
  ContentSelector,
  KnowledgeAssetSelector,
} from '@/components/onboarding/KnowledgeSourceSelectors';
import { KnowledgeRemovalDialog } from '@/components/onboarding/KnowledgeRemovalDialog';
import { TrainingAgentDialog } from '@/components/onboarding/TrainingAgentDialog';
import { useInterviewerSetup } from '@/hooks/useInterviewerSetup';
import { useKnowledgeAssets } from '@/hooks/useKnowledgeAssets';
import contentLibraryService from '@/lib/api/contentLibrary';
import onboardingService from '@/lib/api/onboarding';
import type {
  FileUploadInfo,
  InterviewEntry,
  InterviewerDetail,
  InterviewerPromptImpactSummary,
  RemoveInterviewerKnowledgePayload,
  InterviewerSessionSummary,
  SessionDetailResponse,
} from '@/lib/api/onboarding';
import { cn } from '@/lib/utils';

const getConfidenceColor = (confidence: number) => {
  if (confidence >= 0.7) return 'text-green-600 dark:text-green-400';
  if (confidence >= 0.5) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const getPhaseLabel = (phase: string) => {
  switch (phase) {
    case 'processing':
      return { label: 'Processing', variant: 'secondary' as const };
    case 'planning':
      return { label: 'Planning', variant: 'secondary' as const };
    case 'interviewing':
      return { label: 'In Progress', variant: 'secondary' as const };
    case 'reviewing':
      return { label: 'Reviewing', variant: 'secondary' as const };
    case 'completed':
      return { label: 'Ready', variant: 'default' as const };
    case 'failed':
      return { label: 'Failed', variant: 'destructive' as const };
    default:
      return { label: phase, variant: 'secondary' as const };
  }
};

const formatBatchStatus = (status: string) => status.replace(/_/g, ' ');
const normalizeSourceKey = (value: string) =>
  value.trim().replace(/\/+$/, '').toLowerCase();

const formatOpenQuestion = (question: Record<string, any>) =>
  question.question || question.title || question.topic_name || JSON.stringify(question);

const formatBatchTrigger = (triggerType: string) => {
  switch (triggerType) {
    case 'initial_upload':
      return 'Initial Upload';
    case 'reingest':
      return 'Reingest';
    case 'upload':
      return 'Upload';
    case 'asset_attach':
      return 'Knowledge Update';
    case 'manual_reprocess':
      return 'Manual Reprocess';
    case 'manual_resolution':
      return 'Open Question Review';
    case 'consolidation':
      return 'Consolidation';
    default:
      return triggerType.replace(/_/g, ' ');
  }
};

const getBatchStatusLabel = (status: string) => {
  switch (status) {
    case 'merged':
      return 'Merged';
    case 'completed':
      return 'Ready';
    case 'processing':
      return 'Processing';
    case 'pending':
      return 'Queued';
    case 'failed':
      return 'Failed';
    default:
      return formatBatchStatus(status);
  }
};

const formatPromptDomain = (domain: string) =>
  domain
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getPromptRefreshState = (
  interviewer: Pick<InterviewerDetail, 'processing_status' | 'config_generation_status' | 'prompt_status'>
) => {
  if (interviewer.processing_status === 'processing' || interviewer.processing_status === 'planning') {
    return {
      label: 'Processing knowledge',
      variant: 'secondary' as const,
      description: 'New sources are being extracted and consolidated into canonical knowledge.',
    };
  }
  if (
    interviewer.config_generation_status === 'generating'
    || interviewer.prompt_status === 'regenerating'
  ) {
    return {
      label: 'Updating training',
      variant: 'secondary' as const,
      description: 'Latest canonical knowledge is being applied to the agent configuration.',
    };
  }
  if (interviewer.prompt_status === 'stale') {
    return {
      label: 'Knowledge ready to apply',
      variant: 'secondary' as const,
      description: 'Canonical knowledge changed and is ready for a prompt refresh.',
    };
  }
  if (interviewer.processing_status === 'failed' || interviewer.config_generation_status === 'failed') {
    return {
      label: 'Update failed',
      variant: 'destructive' as const,
      description: 'Something failed while processing knowledge or applying the latest training updates.',
    };
  }
  return {
    label: 'Up to date',
    variant: 'default' as const,
    description: 'Canonical knowledge and agent training are aligned.',
  };
};

const buildSourceSummary = (sources: Array<{ type: string }>) => {
  const parts: string[] = [];
  const knowledgeAssets = sources.filter((source) => source.type !== 'content_video' && source.type !== 'url');
  const contentVideos = sources.filter((source) => source.type === 'content_video');
  const urls = sources.filter((source) => source.type === 'url');

  if (knowledgeAssets.length > 0) {
    parts.push(`${knowledgeAssets.length} knowledge asset${knowledgeAssets.length > 1 ? 's' : ''}`);
  }
  if (contentVideos.length > 0) {
    parts.push(`${contentVideos.length} content video${contentVideos.length > 1 ? 's' : ''}`);
  }
  if (urls.length > 0) {
    parts.push(`${urls.length} URL${urls.length > 1 ? 's' : ''}`);
  }
  return parts.length > 0 ? parts.join(', ') : 'No sources attached';
};

type DisplaySource = {
  key: string;
  type: string;
  label: string;
  status: string;
  sourceKey: 'website' | 'knowledge_base' | 'knowledge_asset' | 'content_video';
  knowledgeAssetId?: string;
  videoId?: string;
};

const buildDisplaySources = ({
  interviewer,
  orgAssets,
  contentVideos,
}: {
  interviewer: InterviewerDetail;
  orgAssets: Array<{
    id: string;
    asset_type: string;
    title: string | null;
    filename: string | null;
    url: string | null;
    processing_status: string;
  }>;
  contentVideos: Array<{
    video_id: string;
    title?: string;
    filename: string;
    status: string;
  }>;
}) => {
  const assetIds = interviewer.knowledge_asset_ids ?? [];
  const videoIds = interviewer.selected_video_ids ?? [];

  const rawSources: DisplaySource[] = [
    ...orgAssets
      .filter((asset) => assetIds.includes(asset.id))
      .map((asset) => ({
        key: asset.id,
        type: asset.asset_type,
        label: asset.title || asset.filename || asset.url || asset.asset_type,
        status: asset.processing_status,
        sourceKey: 'knowledge_asset' as const,
        knowledgeAssetId: asset.id,
      })),
    ...contentVideos
      .filter((video) => videoIds.includes(video.video_id))
      .map((video) => ({
        key: video.video_id,
        type: 'content_video',
        label: video.title || video.filename,
        status: video.status,
        sourceKey: 'content_video' as const,
        videoId: video.video_id,
      })),
    ...(interviewer.website_url
      ? [{
          key: 'website',
          type: 'url',
          label: interviewer.website_url,
          status: 'completed',
          sourceKey: 'website' as const,
        }]
      : []),
    ...(interviewer.knowledge_base_url
      ? [{
          key: 'knowledge-base',
          type: 'url',
          label: interviewer.knowledge_base_url,
          status: 'completed',
          sourceKey: 'knowledge_base' as const,
        }]
      : []),
  ];

  const deduped = new Map<string, (typeof rawSources)[number]>();
  rawSources.forEach((source) => {
    const dedupeKey =
      source.type === 'url'
        ? `url:${normalizeSourceKey(source.label)}`
        : `${source.type}:${normalizeSourceKey(source.label)}`;
    if (!deduped.has(dedupeKey)) {
      deduped.set(dedupeKey, source);
    }
  });

  return Array.from(deduped.values());
};

const buildRemovalPayload = (source: DisplaySource): RemoveInterviewerKnowledgePayload => {
  switch (source.sourceKey) {
    case 'website':
      return { remove_website: true };
    case 'knowledge_base':
      return { remove_knowledge_base: true };
    case 'content_video':
      return source.videoId ? { selected_video_ids: [source.videoId] } : {};
    case 'knowledge_asset':
      return source.knowledgeAssetId ? { knowledge_asset_ids: [source.knowledgeAssetId] } : {};
    default:
      return {};
  }
};

const OverviewMetricCard: React.FC<{
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
}> = ({ label, value, detail }) => (
  <div className="rounded-xl border bg-background p-4">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <div className="mt-2 text-lg font-semibold text-foreground">{value}</div>
    {detail ? <div className="mt-1 text-sm text-muted-foreground">{detail}</div> : null}
  </div>
);

const AttachKnowledgeDialog: React.FC<{
  interviewer: InterviewerDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ interviewer, open, onOpenChange }) => {
  const { ingestKnowledge, isIngesting, uploadFile } = useInterviewerSetup(interviewer.organization_id);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [knowledgeBaseUrl, setKnowledgeBaseUrl] = useState('');
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const resetForm = useCallback(() => {
    setWebsiteUrl('');
    setKnowledgeBaseUrl('');
    setSelectedAssetIds([]);
    setSelectedVideoIds([]);
    setSelectedFiles([]);
  }, []);

  const isBusy = isIngesting || isUploadingFiles;
  const hasChanges =
    !!websiteUrl.trim() ||
    !!knowledgeBaseUrl.trim() ||
    selectedAssetIds.length > 0 ||
    selectedVideoIds.length > 0 ||
    selectedFiles.length > 0;

  const handleOpenChange = (nextOpen: boolean) => {
    if (isBusy) return;
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (!hasChanges || isBusy) return;

    setIsUploadingFiles(true);

    let fileUploads: FileUploadInfo[] | undefined;
    if (selectedFiles.length > 0) {
      const uploaded = await Promise.all(selectedFiles.map((file) => uploadFile(file)));
      if (uploaded.some((file) => !file)) {
        setIsUploadingFiles(false);
        return;
      }
      fileUploads = uploaded.filter(Boolean) as FileUploadInfo[];
    }

    setIsUploadingFiles(false);

    const result = await ingestKnowledge(interviewer.id, {
      websiteUrl: websiteUrl.trim() || undefined,
      knowledgeBaseUrl: knowledgeBaseUrl.trim() || undefined,
      fileUploads,
      selectedVideoIds,
      knowledgeAssetIds: selectedAssetIds,
    });

    if (result) {
      resetForm();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Knowledge</DialogTitle>
          <DialogDescription>
            Add more sources to this training workspace. Once they finish processing, continue the
            training conversation to clarify anything the agent still needs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="attach-website-url">Website URL</Label>
              <Input
                id="attach-website-url"
                type="url"
                placeholder="https://example.com"
                value={websiteUrl}
                onChange={(event) => setWebsiteUrl(event.target.value)}
                disabled={isBusy}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="attach-knowledge-base-url">Knowledge Base URL</Label>
              <Input
                id="attach-knowledge-base-url"
                type="url"
                placeholder="https://docs.example.com"
                value={knowledgeBaseUrl}
                onChange={(event) => setKnowledgeBaseUrl(event.target.value)}
                disabled={isBusy}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="attach-files">Upload Files</Label>
            <Input
              id="attach-files"
              type="file"
              multiple
              accept=".pdf,video/*"
              disabled={isBusy}
              onChange={(event) => setSelectedFiles(Array.from(event.target.files ?? []))}
            />
            {selectedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {selectedFiles.map((file) => (
                  <span
                    key={`${file.name}-${file.size}`}
                    className="rounded-md border bg-muted/50 px-2.5 py-1 text-xs"
                  >
                    {file.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <KnowledgeAssetSelector
            orgId={interviewer.organization_id}
            selectedAssetIds={selectedAssetIds}
            onSelectionChange={setSelectedAssetIds}
            disabled={isBusy}
            excludedAssetIds={interviewer.knowledge_asset_ids ?? []}
          />

          <ContentSelector
            orgId={interviewer.organization_id}
            selectedVideoIds={selectedVideoIds}
            onSelectionChange={setSelectedVideoIds}
            disabled={isBusy}
            excludedVideoIds={interviewer.selected_video_ids ?? []}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isBusy}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!hasChanges || isBusy}>
            {isBusy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Attaching...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Start Ingestion
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OverviewTab: React.FC<{
  interviewer: InterviewerDetail;
  interviewerId: string;
  onGenerationStarted?: () => void;
}> = ({ interviewer, interviewerId, onGenerationStarted }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showAttachDialog, setShowAttachDialog] = useState(false);
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [trainingDialogMode, setTrainingDialogMode] = useState<'general' | 'open_questions'>('general');
  const [sourceToRemove, setSourceToRemove] = useState<DisplaySource | null>(null);
  const { reingestInterviewer, isReingesting } = useInterviewerSetup(interviewer.organization_id);
  const { assets: orgAssets } = useKnowledgeAssets(interviewer.organization_id);
  const { data: videosData } = useQuery({
    queryKey: ['content-library-videos', interviewer.organization_id],
    queryFn: async () => {
      const response = await contentLibraryService.getOrganizationVideos(interviewer.organization_id);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch videos');
      }
      return response.data;
    },
    enabled: !!interviewer.organization_id && (interviewer.selected_video_ids?.length ?? 0) > 0,
  });

  const link = interviewer.interview_link || `${window.location.origin}/interview/${interviewer.token}`;
  const displaySources = useMemo(
    () => {
      const matchedAssets = (interviewer.knowledge_asset_ids ?? []).length > 0
        ? orgAssets.filter((asset) => (interviewer.knowledge_asset_ids ?? []).includes(asset.id))
        : [];
      const matchedVideos = (interviewer.selected_video_ids ?? []).length > 0
        ? (videosData?.videos ?? []).filter((video) =>
            (interviewer.selected_video_ids ?? []).includes(video.video_id)
          )
        : [];

      return buildDisplaySources({
        interviewer,
        orgAssets: matchedAssets,
        contentVideos: matchedVideos,
      });
    },
    [
      interviewer,
      orgAssets,
      videosData?.videos,
    ]
  );
  const sourceSummary = buildSourceSummary(displaySources);
  const openQuestions = (interviewer.knowledge_profile?.open_questions ?? []).reduce<Array<Record<string, any>>>(
    (acc, question) => {
      if (!question || typeof question !== 'object') return acc;
      const formatted = formatOpenQuestion(question);
      if (acc.some((item) => formatOpenQuestion(item) === formatted)) return acc;
      acc.push(question);
      return acc;
    },
    []
  );
  const latestBatch = (interviewer.ingestion_batches ?? [])[0] ?? null;
  const latestBatchStatus = latestBatch ? getBatchStatusLabel(latestBatch.status) : null;
  const promptImpactSummary: InterviewerPromptImpactSummary | null =
    interviewer.prompt_impact_summary ?? latestBatch?.prompt_impact ?? null;
  const promptDomains = (promptImpactSummary?.domains ?? []).slice(0, 4);
  const promptRefreshState = getPromptRefreshState(interviewer);
  const confirmedTopics = Object.entries(
    interviewer.knowledge_profile?.confirmed_topic_summaries ?? {}
  ).sort((left, right) => {
    const leftName = String(left[1]?.topic_name || left[0]);
    const rightName = String(right[1]?.topic_name || right[0]);
    return leftName.localeCompare(rightName);
  });
  const handleReingest = async () => {
    const result = await reingestInterviewer(interviewer.id, 'full_rebuild');
    if (result) {
      queryClient.invalidateQueries({ queryKey: ['interviewer', interviewerId] });
    }
  };

  const handleTrainingUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['interviewer', interviewerId] });
    queryClient.invalidateQueries({ queryKey: ['interviewer-sessions', interviewerId] });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: 'Training link copied' });
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = link;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast({ title: 'Training link copied' });
    }
  };

  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="space-y-5">
      {/* ── Status Strip ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={interviewer.processing_status === 'completed' ? 'default' : 'secondary'}>
            {getPhaseLabel(interviewer.processing_status).label}
          </Badge>
          <Badge variant={promptRefreshState.variant}>{promptRefreshState.label}</Badge>
          {latestBatch && (
            <Badge variant="outline">
              {formatBatchTrigger(latestBatch.trigger_type)} · {latestBatchStatus}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {openQuestions.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setTrainingDialogMode('open_questions');
                setShowTrainingDialog(true);
              }}
              disabled={interviewer.processing_status !== 'completed'}
            >
              <LayoutList className="mr-1.5 h-3.5 w-3.5" />
              Answer {openQuestions.length} Open Q{openQuestions.length === 1 ? '' : 's'}
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => {
              setTrainingDialogMode('general');
              setShowTrainingDialog(true);
            }}
            disabled={interviewer.processing_status !== 'completed'}
          >
            <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
            Train
          </Button>
        </div>
      </div>

      {/* ── Metrics Row ── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <OverviewMetricCard label="Sources" value={displaySources.length} />
        <OverviewMetricCard
          label="Open Questions"
          value={openQuestions.length}
          detail={openQuestions.length > 0 ? (
            <span className="text-amber-500 dark:text-amber-400">Needs attention</span>
          ) : undefined}
        />
        <OverviewMetricCard label="Sessions" value={interviewer.session_count} />
        <OverviewMetricCard
          label="Topics"
          value={confirmedTopics.length}
          detail={confirmedTopics.length > 0 ? 'Confirmed' : undefined}
        />
      </div>

      {/* ── Latest Update ── */}
      {(promptImpactSummary?.summary || openQuestions.length > 0) && (
        <div className="rounded-xl border bg-muted/20 px-4 py-3 text-sm">
          <p className="text-muted-foreground">
            {openQuestions.length > 0
              ? `${openQuestions.length} follow-up question${openQuestions.length === 1 ? '' : 's'} remaining.`
              : promptImpactSummary?.summary || 'No prompt changes detected.'}
          </p>
          {promptDomains.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {promptDomains.map((domain) => (
                <Badge key={domain} variant="secondary" className="text-[10px]">
                  {formatPromptDomain(domain)}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Quick Actions ── */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setShowAttachDialog(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add Knowledge
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleReingest}
          disabled={isReingesting}
        >
          {isReingesting ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
          )}
          Refresh Sources
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCopyLink}>
          <Copy className="mr-1.5 h-3.5 w-3.5" />
          Copy Training Link
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate('/background')}
        >
          <Upload className="mr-1.5 h-3.5 w-3.5" />
          Source Library
        </Button>
      </div>

      {/* ── Attached Sources (collapsed) ── */}
      {displaySources.length > 0 && (
        <div className="rounded-xl border card-border">
          <button
            type="button"
            className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/20 transition-colors"
            onClick={() => setSourcesExpanded((v) => !v)}
          >
            <span className="text-sm font-medium">
              {displaySources.length} attached source{displaySources.length === 1 ? '' : 's'}
            </span>
            <svg
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                sourcesExpanded && 'rotate-180'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {sourcesExpanded && (
            <div className="border-t card-border px-4 py-2 space-y-1">
              {displaySources.map((source) => (
                <div
                  key={`${source.sourceKey}:${source.key}`}
                  className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-muted/10"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm">{source.label}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {source.type === 'url' ? 'URL' : formatPromptDomain(source.type)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="text-[10px]">{getBatchStatusLabel(source.status)}</Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                      onClick={() => setSourceToRemove(source)}
                      disabled={interviewer.processing_status !== 'completed'}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {interviewer.processing_status === 'completed' && (
        <ConfigSection
          interviewer={interviewer}
          interviewerId={interviewerId}
          onGenerationStarted={onGenerationStarted}
        />
      )}

      <AttachKnowledgeDialog
        interviewer={interviewer}
        open={showAttachDialog}
        onOpenChange={setShowAttachDialog}
      />
      <TrainingAgentDialog
        interviewer={interviewer}
        open={showTrainingDialog}
        onOpenChange={setShowTrainingDialog}
        onTrainingUpdated={handleTrainingUpdated}
        mode={trainingDialogMode}
      />
      <KnowledgeRemovalDialog
        organizationId={interviewer.organization_id}
        interviewerId={interviewer.id}
        sourceLabel={sourceToRemove?.label || 'this source'}
        payload={sourceToRemove ? buildRemovalPayload(sourceToRemove) : null}
        open={!!sourceToRemove}
        onOpenChange={(open) => {
          if (!open) setSourceToRemove(null);
        }}
      />
    </div>
  );
};

const KnowledgeTab: React.FC<{
  interviewer: InterviewerDetail;
}> = ({ interviewer }) => {
  const navigate = useNavigate();
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);

  // Separate canonical topics from synthetic/follow-up topics (defensive for old data)
  const confirmedTopics = useMemo(() => {
    const allTopicEntries = Object.entries(
      interviewer.knowledge_profile?.confirmed_topic_summaries ?? {}
    );

    const canonical = allTopicEntries
      .filter(([topicId]) => !topicId.startsWith('open_question_') && topicId !== 'general_followup')
      .sort((left, right) => {
        const leftName = String(left[1]?.topic_name || left[0]);
        const rightName = String(right[1]?.topic_name || right[0]);
        return leftName.localeCompare(rightName);
      });

    const synthetic = allTopicEntries
      .filter(([topicId]) => topicId.startsWith('open_question_') || topicId === 'general_followup')
      .sort((left, right) => {
        const leftName = String(left[1]?.topic_name || left[0]);
        const rightName = String(right[1]?.topic_name || right[0]);
        return leftName.localeCompare(rightName);
      });

    // Show canonical topics first, then any remaining synthetic/follow-up topics
    return [...canonical, ...synthetic];
  }, [interviewer.knowledge_profile?.confirmed_topic_summaries]);
  const selectedTopic =
    confirmedTopics.find(([topicId]) => topicId === selectedTopicId) ?? confirmedTopics[0] ?? null;

  useEffect(() => {
    if (confirmedTopics.length === 0) {
      setSelectedTopicId(null);
      return;
    }
    if (!selectedTopicId || !confirmedTopics.some(([topicId]) => topicId === selectedTopicId)) {
      setSelectedTopicId(confirmedTopics[0][0]);
    }
  }, [confirmedTopics, selectedTopicId]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">Canonical Knowledge</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Inspect what the training workspace has actually confirmed so far.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate('/background')}
        >
          <Upload className="mr-2 h-4 w-4" />
          Open Source Library
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 md:grid-cols-2">
          <OverviewMetricCard
            label="Confirmed Topics"
            value={confirmedTopics.length}
            detail={
              confirmedTopics.length > 0
                ? 'Canonical knowledge merged from sources and training sessions'
                : 'No confirmed knowledge has been merged yet'
            }
          />
          <OverviewMetricCard
            label="Open Questions"
            value={interviewer.knowledge_profile?.open_questions?.length ?? 0}
            detail="Outstanding gaps that still need follow-up"
          />
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Confirmed Topic Summaries</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Read through the exact topic summaries currently shaping the training workspace.
              </p>
            </div>
            {confirmedTopics.length > 0 && (
              <Badge variant="outline">{confirmedTopics.length} topic{confirmedTopics.length === 1 ? '' : 's'}</Badge>
            )}
          </div>

          {confirmedTopics.length > 0 ? (
            <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
              <div className="rounded-xl border bg-muted/10 p-2">
                <div className="space-y-1">
                  {confirmedTopics.map(([topicId, topicData]) => {
                    const topicName = topicData.topic_name || topicId;
                    const isActive = selectedTopic?.[0] === topicId;
                    const isSynthetic = topicId.startsWith('open_question_') || topicId === 'general_followup';

                    return (
                      <button
                        key={topicId}
                        type="button"
                        onClick={() => setSelectedTopicId(topicId)}
                        className={cn(
                          'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors',
                          isActive
                            ? 'bg-background shadow-sm'
                            : 'hover:bg-background/70'
                        )}
                      >
                        <span className={cn(
                          'min-w-0 pr-3 text-sm font-medium',
                          isSynthetic ? 'text-muted-foreground italic' : 'text-foreground'
                        )}>
                          {topicName}
                        </span>
                        <Badge variant={isActive ? 'default' : 'outline'} className="shrink-0">
                          {isSynthetic ? 'Follow-up' : 'Confirmed'}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-xl border bg-background p-5 shadow-sm">
                {selectedTopic ? (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-lg font-semibold text-foreground">
                          {selectedTopic[1].topic_name || selectedTopic[0]}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Canonical summary currently shaping the training workspace.
                        </p>
                      </div>
                      <Badge variant="outline" className="shrink-0">
                        Confirmed
                      </Badge>
                    </div>

                    <div className="mt-5 rounded-lg bg-muted/10 p-4">
                      {typeof selectedTopic[1].summary === 'string' && selectedTopic[1].summary.trim() ? (
                        <div className="prose prose-sm max-w-none text-foreground dark:prose-invert">
                          <Markdown content={selectedTopic[1].summary.trim()} />
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          This area has been confirmed, but no narrative summary has been generated yet.
                        </p>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border bg-muted/10 p-4">
              <p className="text-sm text-muted-foreground">No confirmed knowledge yet.</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const ConfigSection: React.FC<{
  interviewer: InterviewerDetail;
  interviewerId: string;
  onGenerationStarted?: () => void;
}> = ({ interviewer, interviewerId, onGenerationStarted }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const configStatus = interviewer.config_generation_status;
  const promptStatus = interviewer.prompt_status;
  const promptImpactSummary = interviewer.prompt_impact_summary;
  const isApplyingKnowledge = configStatus === 'generating' || promptStatus === 'regenerating';
  const needsPromptRefresh = promptStatus === 'stale';
  const canGenerate = !!interviewer.website_url;

  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await onboardingService.generateConfig(
        interviewer.organization_id,
        interviewer.website_url || ''
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to start config generation');
      }
      return response.data;
    },
    onSuccess: () => {
      onGenerationStarted?.();
      queryClient.invalidateQueries({ queryKey: ['interviewer', interviewerId] });
      toast({ title: 'Config Generation Started', description: 'This may take a few minutes.' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Generation Failed', description: err.message });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Agent Configuration</CardTitle>
      </CardHeader>
      <CardContent>
        {!configStatus && !needsPromptRefresh && (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Create a new agent version from this training session.
            </p>
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || !canGenerate}
              size="sm"
            >
              {generateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Create Agent Version
            </Button>
          </div>
        )}

        {needsPromptRefresh && !isApplyingKnowledge && (
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm">Latest knowledge is ready to apply to your agent.</p>
              <p className="text-sm text-muted-foreground">
                {promptImpactSummary?.summary || 'Canonical knowledge changed after the last applied prompt version.'}
              </p>
            </div>
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || !canGenerate}
              size="sm"
            >
              {generateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Apply Latest Knowledge
            </Button>
          </div>
        )}

        {isApplyingKnowledge && (
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Applying latest training knowledge to the agent...
            </p>
          </div>
        )}

        {configStatus === 'completed' && !needsPromptRefresh && !isApplyingKnowledge && (
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            <p className="text-sm">Training knowledge is applied to your agent.</p>
          </div>
        )}

        {configStatus === 'failed' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-destructive">Config generation failed.</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || !canGenerate}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SessionsTab: React.FC<{
  sessions: InterviewerSessionSummary[];
  isLoading: boolean;
  selectedSessionId: string | null;
  onSelectSession: (id: string) => void;
  sessionDetail: SessionDetailResponse | null;
  sessionDetailLoading: boolean;
  onBackToList: () => void;
}> = ({
  sessions,
  isLoading,
  selectedSessionId,
  onSelectSession,
  sessionDetail,
  sessionDetailLoading,
  onBackToList,
}) => {
  if (selectedSessionId) {
    return (
      <TranscriptTab
        sessionDetail={sessionDetail}
        isLoading={sessionDetailLoading}
        onBack={onBackToList}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="rounded-lg border bg-background p-8">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <Users className="h-8 w-8 opacity-50" />
          <p className="text-sm">No training sessions yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sessions.map((session) => {
        const phase = getPhaseLabel(session.phase);
        const isSelected = session.id === selectedSessionId;

        return (
          <Card
            key={session.id}
            className={cn(
              'cursor-pointer transition-colors',
              isSelected ? 'border-primary' : 'hover:border-primary/40'
            )}
            onClick={() => onSelectSession(session.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm font-medium">
                      {session.interviewee_name || session.interviewee_email || 'Anonymous'}
                    </span>
                    <Badge variant={phase.variant} className="shrink-0 text-xs">
                      {phase.label}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>
                      Topic {session.current_topic_index}/{session.total_topics}
                    </span>
                    <span className={getConfidenceColor(session.overall_confidence)}>
                      {Math.round(session.overall_confidence * 100)}% confidence
                    </span>
                    <span>{new Date(session.created_at).toLocaleDateString()}</span>
                  </div>

                  {session.summary && (
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                      {session.summary}
                    </p>
                  )}
                </div>

                <MessageSquare className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const TranscriptTab: React.FC<{
  sessionDetail: SessionDetailResponse | null;
  isLoading: boolean;
  onBack: () => void;
}> = ({ sessionDetail, isLoading, onBack }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!sessionDetail) {
    return (
      <div className="rounded-lg border bg-background p-8">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <MessageSquare className="h-8 w-8 opacity-50" />
          <p className="text-sm">Select a session to view its transcript</p>
        </div>
      </div>
    );
  }

  const entries = sessionDetail.entries || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Sessions
        </Button>
        <span className="text-sm text-muted-foreground">
          {sessionDetail.interviewee_name || sessionDetail.interviewee_email || 'Anonymous'} ·{' '}
          {entries.length} messages
        </span>
      </div>

      {sessionDetail.summary && (
        <Card>
          <CardContent className="p-4">
            <p className="mb-1 text-xs font-medium text-muted-foreground">Summary</p>
            <p className="whitespace-pre-wrap text-sm">{sessionDetail.summary}</p>
          </CardContent>
        </Card>
      )}

      <div className="max-h-[600px] space-y-4 overflow-y-auto rounded-lg border bg-background p-4">
        {entries.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No messages</p>
        ) : (
          entries.map((entry: InterviewEntry) => (
            <div
              key={entry.id}
              className={cn('flex gap-3', entry.speaker === 'user' && 'flex-row-reverse')}
            >
              <div
                className={cn(
                  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
                  entry.speaker === 'interviewer'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {entry.speaker === 'interviewer' ? (
                  <GraduationCap className="h-4 w-4" />
                ) : (
                  <User className="h-4 w-4" />
                )}
              </div>

              <div
                className={cn(
                  'max-w-[80%] flex-1 rounded-lg px-4 py-2',
                  entry.speaker === 'interviewer'
                    ? 'bg-muted'
                    : 'bg-primary text-primary-foreground'
                )}
              >
                {entry.speaker === 'interviewer' ? (
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    <Markdown content={entry.content} className="text-sm" />
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap text-sm">{entry.content}</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const InterviewerDetailPage: React.FC = () => {
  const { interviewerId } = useParams<{ interviewerId: string }>();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [generationPending, setGenerationPending] = useState(false);

  const {
    data: interviewerData,
    isLoading: interviewerLoading,
    error: interviewerError,
  } = useQuery({
    queryKey: ['interviewer', interviewerId],
    queryFn: async () => {
      if (!interviewerId) throw new Error('Interviewer ID required');
      const response = await onboardingService.getInterviewer(interviewerId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch interviewer');
      }
      return response.data;
    },
    enabled: !!interviewerId,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      const latestBatch = data?.ingestion_batches?.[0];
      const shouldPollIngestion =
        !!data &&
        (['processing', 'planning'].includes(data.processing_status) ||
          (!!latestBatch && !['completed', 'failed'].includes(latestBatch.status)));
      const shouldPollPromptRefresh =
        !!data && (
          data.prompt_status === 'regenerating'
          || data.config_generation_status === 'generating'
          || generationPending
        );

      if (shouldPollPromptRefresh) return 3000;
      if (shouldPollIngestion) return 5000;
      return false;
    },
  });

  useEffect(() => {
    const promptRefreshActive =
      interviewerData?.config_generation_status === 'generating'
      || interviewerData?.prompt_status === 'regenerating';
    if (!promptRefreshActive) {
      setGenerationPending(false);
    }
  }, [interviewerData?.config_generation_status, interviewerData?.prompt_status]);

  const {
    data: sessionsData,
    isLoading: sessionsLoading,
  } = useQuery({
    queryKey: ['interviewer-sessions', interviewerId],
    queryFn: async () => {
      if (!interviewerId) throw new Error('Interviewer ID required');
      const response = await onboardingService.listInterviewerSessions(interviewerId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch sessions');
      }
      return response.data;
    },
    enabled: !!interviewerId,
    retry: false,
  });

  const {
    data: sessionDetail,
    isLoading: sessionDetailLoading,
  } = useQuery({
    queryKey: ['interviewer-session-detail', interviewerId, selectedSessionId],
    queryFn: async () => {
      if (!interviewerId || !selectedSessionId) throw new Error('IDs required');
      const response = await onboardingService.getInterviewerSessionDetail(
        interviewerId,
        selectedSessionId
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch session detail');
      }
      return response.data;
    },
    enabled: !!interviewerId && !!selectedSessionId,
    retry: false,
  });

  const interviewer = interviewerData;
  const sessions = sessionsData?.sessions || [];

  const handleSelectSession = (sessionId: string) => {
    setSelectedSessionId(sessionId);
  };

  const handleBackToSessions = () => {
    setSelectedSessionId(null);
  };

  if (interviewerLoading) {
    return (
      <>
        <Helmet>
          <title>Training Details | Hobbes</title>
        </Helmet>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  if (interviewerError || !interviewer) {
    return (
      <>
        <Helmet>
          <title>Training Details | Hobbes</title>
        </Helmet>
        <div className="px-4 pt-2 sm:px-6 md:px-8">
          <PageHeader
            breadcrumbs={[
              { label: 'Training', href: '/training' },
              { label: 'Details' },
            ]}
          />
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {interviewerError instanceof Error
                ? interviewerError.message
                : 'Failed to load training session details'}
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{interviewer.name || 'Training Session'} | Hobbes</title>
      </Helmet>

      <div className="flex min-h-0 flex-col">
        <PageHeader
          breadcrumbs={[
            { label: 'Training', href: '/training' },
            { label: interviewer.name || 'Training Session', icon: GraduationCap },
          ]}
        />

        <div className="flex-1 px-4 pb-8 sm:px-6 md:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 -ml-3 flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
              <TabsTrigger value="overview">
                <LayoutList className="mr-1.5 h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="knowledge">
                <GraduationCap className="mr-1.5 h-4 w-4" />
                Knowledge
              </TabsTrigger>
              <TabsTrigger value="sessions">
                <Users className="mr-1.5 h-4 w-4" />
                Sessions ({sessions.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <OverviewTab
                interviewer={interviewer}
                interviewerId={interviewerId!}
                onGenerationStarted={() => setGenerationPending(true)}
              />
            </TabsContent>

            <TabsContent value="knowledge">
              <KnowledgeTab interviewer={interviewer} />
            </TabsContent>

            <TabsContent value="sessions">
              <SessionsTab
                sessions={sessions}
                isLoading={sessionsLoading}
                selectedSessionId={selectedSessionId}
                onSelectSession={handleSelectSession}
                sessionDetail={sessionDetail || null}
                sessionDetailLoading={sessionDetailLoading}
                onBackToList={handleBackToSessions}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default InterviewerDetailPage;
